import { useQuery } from "@tanstack/react-query";
import { BadgeAlert, ChevronRight, FileWarning, FileX2, KeyRound } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { daysUntil, expiryText } from "../lib/credentialAlerts";
import { documentExpiryKey } from "../lib/documentStatus";
import { formatDate } from "../lib/format";
import type { AttentionCredential, AttentionData, DocumentRecord } from "../types";

const entityLabel = { vehicle: "Veículo", driver: "Motorista", company: "Empresa" } as const;
const MAX_ROWS = 5;

function DocumentRow({ document, tone }: { document: DocumentRecord; tone: "danger" | "warning" }) {
  const expiry = documentExpiryKey(document.expiryDate);
  const daysLeft = expiry ? daysUntil(expiry) : 0;
  const context = [entityLabel[document.entityType], document.entityIdentifier || document.entityName, document.companyName].filter(Boolean).join(" · ");
  return <li>
    <a className={`attention-row ${tone}`} href={document.url || undefined} target="_blank" rel="noreferrer" aria-label={`Abrir ${document.label}. ${expiryText(daysLeft)}`}>
      <span className="attention-row-icon" aria-hidden="true">{tone === "danger" ? <FileX2/> : <FileWarning/>}</span>
      <span className="attention-row-main"><strong>{document.label}</strong><small>{context}</small></span>
      <span className="attention-row-when"><b>{expiryText(daysLeft)}</b><small>{formatDate(document.expiryDate)}</small></span>
      <ChevronRight size={16} aria-hidden="true"/>
    </a>
  </li>;
}

function CredentialRow({ credential }: { credential: AttentionCredential }) {
  const tone = credential.status === "EXPIRED" ? "danger" : "warning";
  return <li>
    <Link className={`attention-row ${tone}`} to="/credenciais" aria-label={`Credencial ${credential.code} do veículo ${credential.plate}. ${expiryText(credential.daysLeft)}`}>
      <span className="attention-row-icon" aria-hidden="true"><KeyRound/></span>
      <span className="attention-row-main"><strong>{credential.plate}</strong><small>{credential.driverName} · {credential.code}</small></span>
      <span className="attention-row-when"><b>{expiryText(credential.daysLeft)}</b><small>{formatDate(credential.validUntil)}</small></span>
      <ChevronRight size={16} aria-hidden="true"/>
    </Link>
  </li>;
}

function DocumentGroup({ title, status, tone, documents, total, loading }: { title: string; status: "EXPIRED" | "EXPIRING"; tone: "danger" | "warning"; documents: DocumentRecord[]; total: number; loading: boolean }) {
  return <section className="attention-group" aria-label={title}>
    <header><h3>{title}</h3><span className={`attention-count ${tone}`}>{loading ? "…" : total}</span></header>
    {loading ? <div className="attention-skeleton" aria-hidden="true"><i/><i/></div>
      : documents.length ? <ul>{documents.slice(0, MAX_ROWS).map((document) => <DocumentRow key={document.id} document={document} tone={tone}/>)}</ul>
      : <p className="attention-empty">Nenhum documento nesta situação.</p>}
    {total > MAX_ROWS && <Link className="attention-more" to={`/documentos?status=${status}`}>Ver todos os {total} documentos</Link>}
  </section>;
}

/** Dashboard: o que exige ação agora (documentos e credenciais vencidos ou perto de vencer). */
export function AttentionPanel() {
  const attention = useQuery({ queryKey: ["attention"], queryFn: () => api<AttentionData>("/attention"), staleTime: 5 * 60_000 });
  const expired = useQuery({ queryKey: ["documents", "dashboard", "EXPIRED"], queryFn: () => api<DocumentRecord[]>("/documents?status=EXPIRED"), staleTime: 60_000 });
  const expiring = useQuery({ queryKey: ["documents", "dashboard", "EXPIRING"], queryFn: () => api<DocumentRecord[]>("/documents?status=EXPIRING"), staleTime: 60_000 });
  const byExpiry = (a: DocumentRecord, b: DocumentRecord) => String(a.expiryDate || "").localeCompare(String(b.expiryDate || ""));
  const expiredDocuments = [...(expired.data || [])].sort(byExpiry);
  const expiringDocuments = [...(expiring.data || [])].sort(byExpiry);
  const credentials = attention.data?.credentials;
  const credentialItems = credentials?.items || [];

  return <section className="attention" aria-label="Documentos e credenciais que exigem atenção">
    {credentials && credentials.expired > 0 && <div className="alert error attention-alert" role="alert">
      <BadgeAlert size={18} aria-hidden="true"/>
      <span><strong>{credentials.expired === 1 ? "1 credencial vencida." : `${credentials.expired} credenciais vencidas.`}</strong> Veículos com credencial vencida não podem circular: emita uma nova.</span>
      <Link className="button secondary" to="/credenciais">Ver credenciais</Link>
    </div>}
    <div className="attention-grid">
      <article className="card attention-card">
        <div className="card-heading"><div><h2>Documentos</h2><p>Toque em um documento para abri-lo.</p></div><Link to="/documentos">Ver todos</Link></div>
        <DocumentGroup title="Vencidos" status="EXPIRED" tone="danger" documents={expiredDocuments} total={expiredDocuments.length} loading={expired.isLoading}/>
        <DocumentGroup title="A vencer" status="EXPIRING" tone="warning" documents={expiringDocuments} total={expiringDocuments.length} loading={expiring.isLoading}/>
      </article>
      <article className="card attention-card">
        <div className="card-heading"><div><h2>Credenciais</h2><p>Vencidas e que vencem em breve.</p></div><Link to="/credenciais">Ver todas</Link></div>
        <section className="attention-group" aria-label="Credenciais">
          <header><h3>Situação</h3><span className={`attention-count ${credentials?.expired ? "danger" : "warning"}`}>{attention.isLoading ? "…" : (credentials?.expired || 0) + (credentials?.expiring || 0)}</span></header>
          {attention.isLoading ? <div className="attention-skeleton" aria-hidden="true"><i/><i/></div>
            : attention.isError ? <p className="attention-empty">Não foi possível consultar as credenciais agora.</p>
            : credentialItems.length ? <ul>{credentialItems.map((credential) => <CredentialRow key={credential.id} credential={credential}/>)}</ul>
            : <p className="attention-empty">Nenhuma credencial vencida ou a vencer.</p>}
          {credentials && credentials.expired + credentials.expiring > credentialItems.length && <Link className="attention-more" to="/credenciais">Ver todas as credenciais</Link>}
        </section>
      </article>
    </div>
  </section>;
}
